(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 1000px)';
  var SCOPED_HANDLES = new Set(['lumos-firefly', 'lumos-firefly-mini']);
  var instances = new WeakMap();

  function raf(callback) {
    return window.requestAnimationFrame ? window.requestAnimationFrame(callback) : window.setTimeout(callback, 0);
  }

  function cancelRaf(id) {
    if (id === null || id === undefined) return;
    if (window.cancelAnimationFrame) {
      window.cancelAnimationFrame(id);
    } else {
      window.clearTimeout(id);
    }
  }

  function normaliseHandle(value) {
    return String(value || '').trim().toLowerCase();
  }

  class LightPurchaseFlow {
    constructor(root) {
      this.root = root;
      this.handle = normaliseHandle(root.dataset.lightProductHandle);
      this.wrapper = root.querySelector('[data-light-purchase-wrapper]') || root.querySelector('.Product__Wrapper');
      this.galleryContainer = root.querySelector('[data-light-gallery-container]') || root.querySelector('.product-img-container');
      this.rail = root.querySelector('[data-light-purchase-rail]') || root.querySelector('.Product__InfoWrapper');
      this.form = root.querySelector('.ProductForm');
      this.header = document.getElementById('shopify-section-header');
      this.mobileMedia = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : { matches: false };
      this.compactTriggerY = 0;
      this.expandTriggerY = 0;
      this.compactHeight = 220;
      this.compensation = 0;
      this.scrollFrame = null;
      this.syncFrame = null;
      this.resizeTimer = null;
      this.mobileMounted = false;
      this.isCompact = false;
      this.isReleased = false;
      this.floatingBar = null;
      this.observedNativeButton = null;
      this.nativeButtonIsVisible = false;
      this.nativeButtonObserver = null;
      this.footer = null;
      this.storyBoundary = null;
      this.quantityStates = [];
      this.legacyQuickAddStates = [];
      this.desktopGalleryMainSwiper = null;
      this.desktopGalleryThumbsSwiper = null;
      this.destroyed = false;
      this.ready = false;

      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleGalleryWheel = this.handleGalleryWheel.bind(this);
      this.handleFormChange = this.handleFormChange.bind(this);
      this.handleFormInput = this.handleFormInput.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.syncFloatingBar = this.syncFloatingBar.bind(this);
      this.handleFloatingClick = this.handleFloatingClick.bind(this);
      this.handleDocumentStateChange = this.handleDocumentStateChange.bind(this);

      if (
        !SCOPED_HANDLES.has(this.handle) ||
        !this.wrapper ||
        !this.galleryContainer ||
        !this.rail ||
        !this.form
      ) {
        return;
      }

      this.setupBoundaries();
      this.disableLegacyQuickAdd();
      this.createPlaceholders();
      this.createFloatingBar();
      this.captureQuantityState();
      this.bind();
      this.ensureInitialConfiguration();
      this.applyResponsiveState();
      this.scheduleSync();
      root.dataset.lightPurchaseFlowReady = 'true';
      this.ready = true;
    }

    setupBoundaries() {
      var host = this.root.closest('[id^="shopify-section-"]');
      this.storyBoundary = host && host.nextElementSibling ? host.nextElementSibling : null;
      this.footer = document.querySelector('footer, [data-section-type="footer"], [id*="__footer"]');
    }

    disableLegacyQuickAdd() {
      var shell = this.root.closest('[data-light-purchase-shell]');
      if (!shell) return;
      var sections = shell.querySelectorAll('[id*="quick_add_to_cart_v2"]');
      sections.forEach(function (section) {
        if (!this.legacyQuickAddStates.some(function (state) { return state.section === section; })) {
          this.legacyQuickAddStates.push({
            section: section,
            display: section.style.getPropertyValue('display'),
            displayPriority: section.style.getPropertyPriority('display'),
            ariaHidden: section.getAttribute('aria-hidden'),
            inert: section.inert
          });
        }
        this.lockLegacyQuickAdd(section);
      }, this);
      if (!sections.length || !window.MutationObserver) return;
      this.legacyQuickAddObserver = new MutationObserver(function () {
        this.legacyQuickAddStates.forEach(function (state) {
          this.lockLegacyQuickAdd(state.section);
        }, this);
      }.bind(this));
      sections.forEach(function (section) {
        this.legacyQuickAddObserver.observe(section, {
          attributes: true,
          attributeFilter: ['style', 'aria-hidden', 'hidden']
        });
      }, this);
    }

    lockLegacyQuickAdd(section) {
      if (section.style.getPropertyValue('display') !== 'none' || section.style.getPropertyPriority('display') !== 'important') {
        section.style.setProperty('display', 'none', 'important');
      }
      if (section.getAttribute('aria-hidden') !== 'true') section.setAttribute('aria-hidden', 'true');
      if (!section.inert) section.inert = true;
    }

    createPlaceholders() {
      this.sentinel = document.createElement('span');
      this.sentinel.dataset.lightGallerySentinel = 'true';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.compensationElement = document.createElement('span');
      this.compensationElement.dataset.lightGalleryCompensation = 'true';
      this.compensationElement.setAttribute('aria-hidden', 'true');
    }

    createFloatingBar() {
      var bar = document.createElement('aside');
      bar.dataset.lightFloatingBar = 'true';
      bar.dataset.lightVisible = 'false';

      var inner = document.createElement('div');
      inner.className = 'light-floating-inner';
      var image = document.createElement('img');
      image.className = 'light-floating-thumbnail';
      image.alt = '';
      image.width = 48;
      image.height = 48;
      var summary = document.createElement('div');
      summary.className = 'light-floating-summary';
      var configuration = document.createElement('span');
      configuration.className = 'light-floating-configuration';
      var price = document.createElement('span');
      price.className = 'light-floating-price';
      var button = document.createElement('button');
      button.className = 'light-floating-button';
      button.type = 'button';

      summary.append(configuration, price);
      inner.append(image, summary, button);
      bar.append(inner);
      document.body.append(bar);
      button.addEventListener('click', this.handleFloatingClick);
      this.floatingBar = bar;
      this.floatingImage = image;
      this.floatingConfiguration = configuration;
      this.floatingPrice = price;
      this.floatingButton = button;
    }

    captureQuantityState() {
      this.form.querySelectorAll('[name="quantity"]').forEach(function (input) {
        this.quantityStates.push({
          input: input,
          value: input.value,
          readOnly: input.readOnly
        });
        input.value = '1';
        input.readOnly = true;
      }, this);
    }

    bind() {
      this.form.addEventListener('change', this.handleFormChange);
      this.form.addEventListener('input', this.handleFormInput);
      this.form.addEventListener('variant:changed', this.handleVariantChanged);
      document.addEventListener('variant:changed', this.handleVariantChanged);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      document.addEventListener('cart:refresh', this.handleDocumentStateChange);
      document.addEventListener('cart:updated', this.handleDocumentStateChange);
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.galleryContainer.addEventListener('wheel', this.handleGalleryWheel, { capture: true, passive: false });

      if (this.mobileMedia.addEventListener) {
        this.mobileMedia.addEventListener('change', this.handleMediaChange);
      } else if (this.mobileMedia.addListener) {
        this.mobileMedia.addListener(this.handleMediaChange);
      }

      if (window.MutationObserver) {
        this.rootObserver = new MutationObserver(this.scheduleSync.bind(this));
        var priceList = this.form.querySelector('.ProductMeta__PriceList');
        var buyButtons = this.form.querySelector('.ProductForm__BuyButtons');
        if (priceList) {
          this.rootObserver.observe(priceList, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class', 'data-price']
          });
        }
        if (buyButtons) {
          this.rootObserver.observe(buyButtons, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class', 'disabled']
          });
        }

        this.documentObserver = new MutationObserver(this.handleDocumentStateChange);
        this.documentObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class']
        });
        document.querySelectorAll('#sidebar-cart, [data-cart-drawer], .icartDrawer').forEach(function (drawer) {
          this.documentObserver.observe(drawer, {
            attributes: true,
            attributeFilter: ['class', 'aria-hidden', 'open']
          });
        }, this);
      }
      this.observeNativeButton();
    }

    handleSectionUnload(event) {
      if (event.target && (event.target === this.root || event.target.contains?.(this.root))) {
        this.destroy();
      }
    }

    ensureInitialConfiguration() {
      if (new URLSearchParams(window.location.search).has('variant')) return;

      var configurationInputs = Array.from(
        this.form.querySelectorAll('.ProductForm__Variants input[type="radio"]')
      ).filter(function (input) {
        return !input.disabled && !input.classList.contains('addon') && !input.closest('.addon');
      });
      if (!configurationInputs.length) return;

      var checked = configurationInputs.find(function (input) { return input.checked; });
      var first = configurationInputs[0];
      if (checked === first) return;

      first.checked = true;
      first.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleDocumentStateChange() {
      this.updateFloatingVisibility();
    }

    handleMediaChange() {
      this.applyResponsiveState();
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(function () {
        if (this.destroyed) return;
        if (this.mobileMedia.matches) {
          this.removeCompactState();
          this.measureGallery();
          this.updateScrollState();
        }
        this.syncFloatingBarHeight();
      }.bind(this), 100);
    }

    handleScroll() {
      if (this.scrollFrame !== null) return;
      this.scrollFrame = raf(function () {
        this.scrollFrame = null;
        this.updateScrollState();
      }.bind(this));
    }

    handleGalleryWheel(event) {
      if (!this.mobileMedia.matches || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      event.stopPropagation();
      window.scrollBy({ top: event.deltaY, left: 0, behavior: 'auto' });
    }

    handleFormChange(event) {
      if (event.target && event.target.name === 'quantity') {
        event.target.value = '1';
      }
      this.scheduleSync();
    }

    handleFormInput(event) {
      if (event.target && event.target.name === 'quantity') {
        event.target.value = '1';
      }
    }

    handleVariantChanged(event) {
      if (event.target && !this.root.contains(event.target)) return;
      this.scheduleSync();
    }

    scheduleSync() {
      if (this.syncFrame !== null) return;
      this.syncFrame = raf(function () {
        this.syncFrame = null;
        if (this.destroyed) return;
        this.form.querySelectorAll('[name="quantity"]').forEach(function (input) {
          input.value = '1';
          input.readOnly = true;
        });
        this.observeNativeButton();
        this.syncFloatingBar();
      }.bind(this));
    }

    headerOffset() {
      if (!this.header || this.header.classList.contains('minimised')) return 0;
      var rect = this.header.getBoundingClientRect();
      return Math.max(0, Math.min(Math.round(rect.height), Math.round(rect.bottom)));
    }

    createMobilePlaceholders() {
      if (!this.sentinel.isConnected) this.galleryContainer.before(this.sentinel);
      if (!this.compensationElement.isConnected) this.galleryContainer.after(this.compensationElement);
    }

    removeMobilePlaceholders() {
      this.sentinel.remove();
      this.compensationElement.remove();
      this.root.style.removeProperty('--light-gallery-compensation');
      this.root.style.removeProperty('--light-sticky-top');
    }

    syncFloatingBarHeight() {
      if (!this.mobileMedia.matches || !this.floatingBar) {
        this.root.style.removeProperty('--light-floating-bar-height');
        return;
      }
      var height = this.floatingBar.dataset.lightVisible === 'true'
        ? this.floatingBar.getBoundingClientRect().height
        : 0;
      this.root.style.setProperty('--light-floating-bar-height', `${Math.round(height)}px`);
    }

    measureGallery() {
      if (!this.mobileMedia.matches) return;
      var wasCompact = this.isCompact;
      if (wasCompact) this.removeCompactState();

      this.createMobilePlaceholders();
      var expandedHeight = Math.round(this.galleryContainer.getBoundingClientRect().height);
      var computedHeight = Number.parseFloat(
        window.getComputedStyle(this.root).getPropertyValue('--light-gallery-height')
      );
      this.compactHeight = Number.isFinite(computedHeight) ? computedHeight : 220;
      this.compensation = Math.max(0, expandedHeight - this.compactHeight);
      var galleryTop = window.scrollY + this.galleryContainer.getBoundingClientRect().top;
      this.compactTriggerY = Math.max(0, galleryTop + this.compensation - this.headerOffset());
      this.expandTriggerY = Math.max(0, galleryTop - this.headerOffset());
      this.root.style.setProperty('--light-gallery-compensation', `${this.compensation}px`);
      this.root.style.setProperty('--light-sticky-top', `${this.headerOffset()}px`);

      if (wasCompact) this.applyCompactState(true);
    }

    storyIsVisible() {
      if (this.storyBoundary) {
        var boundaryRect = this.storyBoundary.getBoundingClientRect();
        return boundaryRect.top <= this.headerOffset() + this.compactHeight;
      }
      var rootRect = this.root.getBoundingClientRect();
      return rootRect.bottom <= window.innerHeight;
    }

    applyCompactState(isCompact) {
      this.isCompact = Boolean(isCompact);
      this.root.classList.toggle('is-light-gallery-compact', this.isCompact);
      this.root.style.setProperty('--light-sticky-top', `${this.headerOffset()}px`);
      if (this.isCompact) {
        this.compensationElement.style.height = `${this.compensation}px`;
      } else {
        this.compensationElement.style.height = '0px';
      }
      this.updateFloatingVisibility();
    }

    removeCompactState() {
      this.applyCompactState(false);
    }

    updateScrollState() {
      if (!this.mobileMedia.matches || this.destroyed) return;
      this.root.style.setProperty('--light-sticky-top', `${this.headerOffset()}px`);
      var storyVisible = this.storyIsVisible();
      if (!this.isReleased && storyVisible && window.scrollY > this.compactTriggerY) {
        this.isReleased = true;
        this.removeCompactState();
      } else if (!storyVisible && this.isReleased && window.scrollY < this.compactTriggerY) {
        this.isReleased = false;
      }

      if (!this.isReleased) {
        if (!this.isCompact && window.scrollY >= this.compactTriggerY) {
          this.applyCompactState(true);
        } else if (this.isCompact && window.scrollY <= this.expandTriggerY + 8) {
          this.applyCompactState(false);
        }
      }
      this.updateFloatingVisibility();
    }

    applyResponsiveState() {
      if (this.destroyed) return;
      if (this.mobileMedia.matches) {
        this.mountMobile();
      } else {
        this.mountDesktop();
      }
    }

    mountMobile() {
      this.unmountDesktopGallery();
      if (!this.mobileMounted) {
        this.mobileMounted = true;
        document.body.classList.add('light-purchase-flow-active');
        this.createMobilePlaceholders();
      }
      this.isReleased = false;
      this.removeCompactState();
      this.measureGallery();
      this.updateScrollState();
      this.syncFloatingBarHeight();
    }

    mountDesktop() {
      if (this.mobileMounted || this.root.classList.contains('is-light-gallery-compact')) {
        this.mobileMounted = false;
        this.isReleased = false;
        this.removeCompactState();
        this.removeMobilePlaceholders();
        document.body.classList.remove('light-purchase-flow-active');
        if (this.floatingBar) this.floatingBar.dataset.lightVisible = 'false';
        this.root.style.removeProperty('--light-floating-bar-height');
      }
      this.mountDesktopGallery();
    }

    mountDesktopGallery() {
      if (this.handle !== 'lumos-firefly' || !window.Swiper || this.desktopGalleryMainSwiper || this.desktopGalleryThumbsSwiper) return;
      var gallery = this.root.querySelector('[data-light-desktop-gallery]');
      var main = gallery?.querySelector('[data-light-gallery-main]');
      var thumbs = gallery?.querySelector('[data-light-gallery-thumbs]');
      var thumbsContainer = thumbs?.querySelector('.swiper-container');
      if (!gallery || !main || !thumbsContainer || !thumbsContainer.querySelector('.swiper-slide')) return;
      var visibleThumbs = Math.max(1, Math.floor((thumbsContainer.clientHeight || 420) / 70));
      this.desktopGalleryThumbsSwiper = new window.Swiper(thumbsContainer, {
        direction: 'vertical',
        loop: false,
        speed: 400,
        spaceBetween: 10,
        slidesPerView: visibleThumbs,
        slidesPerGroup: visibleThumbs,
        watchOverflow: true,
        navigation: {
          nextEl: gallery.querySelector('.sync2_slider__next'),
          prevEl: gallery.querySelector('.sync2_slider__prev')
        }
      });
      this.desktopGalleryMainSwiper = new window.Swiper(main, {
        direction: 'horizontal',
        loop: false,
        speed: 400,
        slidesPerView: 1,
        centeredSlides: true,
        allowTouchMove: true,
        watchOverflow: true,
        navigation: {
          nextEl: gallery.querySelector('.gallery-navigation-wrapper .slider__next'),
          prevEl: gallery.querySelector('.gallery-navigation-wrapper .slider__prev')
        },
        thumbs: {
          swiper: this.desktopGalleryThumbsSwiper,
          slideThumbActiveClass: 'swiper-slide-thumb-active'
        }
      });
    }

    unmountDesktopGallery() {
      this.desktopGalleryMainSwiper?.destroy(true, true);
      this.desktopGalleryThumbsSwiper?.destroy(true, true);
      this.desktopGalleryMainSwiper = null;
      this.desktopGalleryThumbsSwiper = null;
    }

    selectedConfiguration() {
      var bundleSelection = this.form.querySelector(
        '.ProductForm__BundleOption input[type="radio"]:checked'
      );
      if (bundleSelection) {
        var bundleValue = String(bundleSelection.value || '').trim();
        return /^\d+$/.test(bundleValue) ? `${bundleValue}x` : bundleValue || '—';
      }

      var inputs = Array.from(this.form.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'))
        .filter(function (input) {
          return !input.classList.contains('addon') && !input.closest('.addon') && !input.closest('.addon-extra');
        });
      var preferred = inputs.find(function (input) {
        var variant = normaliseHandle(input.dataset.variant);
        return ['lights', 'select-a-bundle', 'model', 'configuration', 'bundle'].includes(variant);
      });
      var selected = preferred || inputs.find(function (input) {
        return input.closest('.ProductForm__Variants');
      }) || inputs[0];
      if (!selected) return '—';
      var value = String(selected.value || '').trim();
      return /^\d+$/.test(value) ? `${value}x` : value || '—';
    }

    selectedPrice() {
      var price = this.form.querySelector(
        '.ProductMeta__PriceList .Price--highlight, .ProductMeta__PriceList .ProductMeta__Price:not(.Price--compareAt)'
      );
      if (price && price.textContent.trim()) return price.textContent.trim();

      var selected = this.form.querySelector('.ProductForm__Variants input[type="radio"]:checked');
      var optionPrice = selected && selected.closest('li')?.querySelector('.model-price, .ProductMeta__Price');
      return optionPrice ? optionPrice.textContent.trim() : '—';
    }

    parseDisplayedMoney(value) {
      var cleaned = String(value || '').replace(/[^\d,.-]/g, '');
      var lastDot = cleaned.lastIndexOf('.');
      var lastComma = cleaned.lastIndexOf(',');
      var decimalIndex = Math.max(lastDot, lastComma);
      if (decimalIndex >= 0 && cleaned.length - decimalIndex - 1 <= 2) {
        var whole = cleaned.slice(0, decimalIndex).replace(/[.,]/g, '');
        var fraction = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, '');
        cleaned = `${whole}.${fraction}`;
      } else {
        cleaned = cleaned.replace(/[.,]/g, '');
      }
      var amount = Number.parseFloat(cleaned);
      return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    }

    selectedAddonTotal() {
      return Array.from(this.form.querySelectorAll('.SizeSwatch__Radio.addon:checked')).reduce(function (total, input) {
        var price = input.closest('li')?.querySelector('[data-price]');
        var cents = price ? this.parseDisplayedMoney(price.dataset.price) : null;
        return total + (cents || 0);
      }.bind(this), 0);
    }

    formatMoney(cents, fallback) {
      var currency = window.Shopify?.currency?.active;
      if (!Number.isFinite(cents) || !currency || !window.Intl?.NumberFormat) return fallback;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency
        }).format(cents / 100);
      } catch (error) {
        return fallback;
      }
    }

    selectedTotalPrice() {
      var mainPrice = this.selectedPrice();
      var addonTotal = this.selectedAddonTotal();
      if (!addonTotal) return mainPrice;
      var mainCents = this.parseDisplayedMoney(mainPrice);
      return mainCents === null ? mainPrice : this.formatMoney(mainCents + addonTotal, mainPrice);
    }

    selectedImage() {
      var image = this.root.querySelector(
        '.product-img-panel.mobile-view .product-showcase.active img, .product-img-panel.mobile-view .gallery-main img, .product-img-panel.desktop-view .product-showcase.active img'
      );
      if (!image) image = this.root.querySelector('.product-img-container img');
      return image;
    }

    nativeButton() {
      return this.form.querySelector('.ProductForm__BuyButtons .ProductForm__AddToCart:not(.mobile), .ProductForm__BuyButtons .ProductForm__AddToCart');
    }

    observeNativeButton() {
      var native = this.nativeButton();
      if (native === this.observedNativeButton) return;
      this.nativeButtonObserver?.disconnect();
      this.observedNativeButton = native;
      this.nativeButtonIsVisible = false;
      if (!native || !window.IntersectionObserver) return;
      this.nativeButtonObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        this.nativeButtonIsVisible = Boolean(entry && entry.isIntersecting && entry.intersectionRatio > 0);
        this.updateFloatingVisibility();
      }.bind(this), { threshold: 0 });
      this.nativeButtonObserver.observe(native);
    }

    syncFloatingBar() {
      if (!this.floatingBar) return;
      this.floatingConfiguration.textContent = this.selectedConfiguration();
      this.floatingPrice.textContent = this.selectedTotalPrice();
      var image = this.selectedImage();
      if (image) {
        this.floatingImage.src = image.currentSrc || image.src;
        this.floatingImage.alt = image.alt || '';
      }
      var native = this.nativeButton();
      this.floatingButton.disabled = !native || native.disabled;
      this.floatingButton.textContent = native
        ? native.textContent.trim()
        : (window.languages?.productFormAddToCart || '');
      this.syncFloatingBarHeight();
    }

    cartIsOpen() {
      return Boolean(
        document.body.classList.contains('cart-open') ||
        document.body.classList.contains('icart-open') ||
        document.querySelector('[data-cart-drawer][aria-hidden="false"], .CartDrawer.is-open, #sidebar-cart[aria-hidden="false"], .icartDrawer.open')
      );
    }

    footerIsVisible() {
      if (!this.footer) return false;
      var rect = this.footer.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    updateFloatingVisibility() {
      if (!this.floatingBar) return;
      var shouldShow = this.mobileMedia.matches &&
        this.mobileMounted &&
        window.scrollY >= this.compactTriggerY + 24 &&
        (this.isCompact || this.isReleased) &&
        !this.nativeButtonIsVisible &&
        !this.cartIsOpen() &&
        !this.footerIsVisible();
      this.floatingBar.dataset.lightVisible = shouldShow ? 'true' : 'false';
      this.syncFloatingBarHeight();
    }

    handleFloatingClick(event) {
      event.preventDefault();
      var native = this.nativeButton();
      if (!native || native.disabled) return;
      native.click();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      cancelRaf(this.scrollFrame);
      cancelRaf(this.syncFrame);
      window.clearTimeout(this.resizeTimer);
      this.form.removeEventListener('change', this.handleFormChange);
      this.form.removeEventListener('input', this.handleFormInput);
      this.form.removeEventListener('variant:changed', this.handleVariantChanged);
      document.removeEventListener('variant:changed', this.handleVariantChanged);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      document.removeEventListener('cart:refresh', this.handleDocumentStateChange);
      document.removeEventListener('cart:updated', this.handleDocumentStateChange);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);
      this.galleryContainer.removeEventListener('wheel', this.handleGalleryWheel, true);
      if (this.mobileMedia.removeEventListener) {
        this.mobileMedia.removeEventListener('change', this.handleMediaChange);
      } else if (this.mobileMedia.removeListener) {
        this.mobileMedia.removeListener(this.handleMediaChange);
      }
      this.rootObserver?.disconnect();
      this.documentObserver?.disconnect();
      this.nativeButtonObserver?.disconnect();
      this.legacyQuickAddObserver?.disconnect();
      this.unmountDesktopGallery();
      this.removeCompactState();
      this.removeMobilePlaceholders();
      this.quantityStates.forEach(function (state) {
        state.input.value = state.value;
        state.input.readOnly = state.readOnly;
      });
      this.legacyQuickAddStates.forEach(function (state) {
        if (state.display) {
          state.section.style.setProperty('display', state.display, state.displayPriority);
        } else {
          state.section.style.removeProperty('display');
        }
        if (state.ariaHidden === null) {
          state.section.removeAttribute('aria-hidden');
        } else {
          state.section.setAttribute('aria-hidden', state.ariaHidden);
        }
        state.section.inert = state.inert;
      });
      this.floatingButton?.removeEventListener('click', this.handleFloatingClick);
      this.floatingBar?.remove();
      document.body.classList.remove('light-purchase-flow-active');
      delete this.root.dataset.lightPurchaseFlowReady;
      instances.delete(this.root);
    }
  }

  function mount(root) {
    if (!root || instances.has(root)) return instances.get(root);
    var instance = new LightPurchaseFlow(root);
    if (!instance.ready) return null;
    instances.set(root, instance);
    return instance;
  }

  function mountAll(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    container.querySelectorAll('[data-light-purchase-flow]').forEach(mount);
    if (container.matches && container.matches('[data-light-purchase-flow]')) mount(container);
  }

  function boot() {
    mountAll(document);
    document.addEventListener('shopify:section:load', function (event) {
      mountAll(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.LightPurchaseFlow = LightPurchaseFlow;
})();
